/**
 * ToDoListPage — personal TODO list (migration 145 model).
 *
 * Data model
 * ----------
 * Items live in the new `todo_items` table (GET /api/todo-items). Each row
 * is either:
 *   - Standalone (own title/description, no project link)
 *   - Tagged to a company/project
 *   - A pointer to an existing task/sub-task (linked_task_id /
 *     linked_subtask_id)
 *
 * Sub-items use `parent_todo_id` for one-level hierarchy. Items are
 * bucketed by user-defined groups (GET /api/todo-groups) — "Today",
 * "This Week" etc. are seeded for each user.
 *
 * Interactions
 * ------------
 * - Click "+ New TODO" → inline form (title required, optional
 *   description / company / project / group)
 * - Click "+ Add existing" → search modal over existing tasks/sub-tasks
 * - Click "+ Group" → rename/colour/order existing groups, add new
 * - Right-click a TODO row → context menu (set %, add sub-TODO, edit,
 *   move to group, delete)
 * - Drag-handle reorders items within a group
 *
 * Layout
 * ------
 * Sections by group. Each section header shows count + colour. Items
 * render with completion bar, hierarchy indent for sub-TODOs, and
 * contextual chips for company/project/linked-source.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box, Flex, HStack, VStack, Text, Button, IconButton, Input, Textarea,
  Spinner, Badge, useToast, Select, Modal, ModalOverlay, ModalContent,
  ModalHeader, ModalBody, ModalFooter, ModalCloseButton, Tooltip, Divider,
  Progress, FormControl, FormLabel, Tag, TagLabel, useDisclosure,
} from '@chakra-ui/react';
import {
  FiPlus, FiCheck, FiTrash2, FiEdit2, FiSearch, FiArrowLeft, FiMoreHorizontal,
  FiFolder, FiBriefcase, FiLink, FiCornerDownRight, FiX, FiSettings,
  FiClock, FiPause, FiPlay, FiCalendar, FiMove, FiEye, FiEyeOff,
} from 'react-icons/fi';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const sameId = (a, b) => a != null && b != null && String(a) === String(b);

// Formats a date+time into a short human-friendly deadline label.
// Returns null for items with no deadline so the deadline cell stays empty.
function formatDeadline(dateStr, timeStr) {
  if (!dateStr) return null;
  // Date column comes back from MySQL as "YYYY-MM-DD" or a Date object;
  // normalise to a Date at noon local to avoid timezone-shift surprises.
  const datePart = typeof dateStr === 'string' ? dateStr.slice(0, 10) : dateStr.toISOString().slice(0, 10);
  const d = new Date(`${datePart}T12:00:00`);
  if (isNaN(d.getTime())) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const that  = new Date(d);  that.setHours(0, 0, 0, 0);
  const diffDays = Math.round((that - today) / 86400000);
  let label;
  if (diffDays === 0)      label = 'Today';
  else if (diffDays === 1) label = 'Tomorrow';
  else if (diffDays === -1) label = 'Yesterday';
  else if (diffDays > 0 && diffDays < 7)  label = d.toLocaleDateString('en-GB', { weekday: 'short' });
  else                     label = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  if (timeStr) {
    // timeStr from MySQL TIME is "HH:MM:SS" — trim to HH:MM.
    label += ' ' + String(timeStr).slice(0, 5);
  }
  return { label, isOverdue: diffDays < 0, isToday: diffDays === 0, raw: d };
}

// Sortable comparator for items in a section, controlled by `sortBy`.
// "deadline"  — earliest first, undated items sink to the bottom
// "added"     — newest first
// "company"   — alphabetical by company name (blanks last)
// "manual"    — respect user's sort_order (the drag-reorder result)
function compareItems(a, b, sortBy) {
  if (sortBy === 'deadline') {
    const ad = a.deadline_date ? new Date(a.deadline_date + (a.deadline_time ? 'T' + String(a.deadline_time).slice(0, 5) : 'T23:59')) : null;
    const bd = b.deadline_date ? new Date(b.deadline_date + (b.deadline_time ? 'T' + String(b.deadline_time).slice(0, 5) : 'T23:59')) : null;
    if (ad && bd) return ad - bd;
    if (ad) return -1;
    if (bd) return 1;
    return (a.sort_order || 0) - (b.sort_order || 0);
  }
  if (sortBy === 'added') {
    const ac = a.created_at ? new Date(a.created_at) : new Date(0);
    const bc = b.created_at ? new Date(b.created_at) : new Date(0);
    return bc - ac; // newest first
  }
  if (sortBy === 'company') {
    const an = (a.company_name || '').toLowerCase();
    const bn = (b.company_name || '').toLowerCase();
    if (an && bn && an !== bn) return an.localeCompare(bn);
    if (an && !bn) return -1;
    if (!an && bn) return 1;
    return (a.sort_order || 0) - (b.sort_order || 0);
  }
  // manual
  return (a.sort_order || 0) - (b.sort_order || 0);
}

function classifyItems(items, sortBy) {
  // Top-level items in order, with their children attached as `.children`.
  const byParent = new Map();
  items.forEach(it => {
    const key = it.parent_todo_id || '__root__';
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key).push(it);
  });
  const top = (byParent.get('__root__') || []).slice().sort((a, b) => compareItems(a, b, sortBy));
  top.forEach(t => {
    // Children always sort by their own sort_order — their "place" is
    // beneath the parent regardless of the top-level sort.
    t.children = (byParent.get(t.id) || []).slice().sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  });
  return top;
}

// ─── Top-level page ──────────────────────────────────────────────────────────
export default function ToDoListPage({ onBack, onNavigateToProject }) {
  const { user } = useAuth();
  const toast = useToast();

  // ── User-switching (admin/feature-gated) ─────────────────────────────────
  // Default to the caller's own list. If the see_others_todo admin setting
  // is on (default) OR the caller is admin, a dropdown lets them pick any
  // other user — the page then reads /todo-items?user_id=… The page is
  // read-only when viewing someone else's list (we don't ship POST/PUT
  // permissions to other users' rows; the backend would reject anyway).
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null); // null = self
  const [canPickUser, setCanPickUser] = useState(false);
  const isAdmin = user?.role === 'admin';
  const viewingOther = selectedUserId && selectedUserId !== user?.id;

  // Load the see_others_todo setting once. Setting absent OR 'true' → on.
  // Admins always have access regardless.
  useEffect(() => {
    let cancelled = false;
    api.get('/settings/see_others_todo')
      .then(r => {
        if (cancelled) return;
        const v = r.data?.setting_value;
        const enabled = v == null ? true : String(v).toLowerCase() !== 'false';
        setCanPickUser(enabled || isAdmin);
      })
      .catch(() => { if (!cancelled) setCanPickUser(isAdmin || true); });
    return () => { cancelled = true; };
  }, [isAdmin]);

  // Fetch the user list whenever the picker is available.
  useEffect(() => {
    if (!canPickUser) return;
    let cancelled = false;
    api.get('/calendar/users')
      .then(r => { if (!cancelled) setUsers(Array.isArray(r.data) ? r.data : []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [canPickUser]);

  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDone, setShowDone] = useState(false);
  // Manually-hidden items are kept separate from done-hidden items so the
  // user can reveal one category without the other. Two independent
  // toggles in the header drive the corresponding query params on /todo-items.
  const [showHidden, setShowHidden] = useState(false);
  // Sort persists in localStorage so the user's preference survives reloads
  // without needing a server-side per-user setting.
  const [sortBy, setSortBy] = useState(() => {
    try { return localStorage.getItem('todo_sort_by') || 'manual'; }
    catch (e) { return 'manual'; }
  });
  useEffect(() => {
    try { localStorage.setItem('todo_sort_by', sortBy); } catch (e) {}
  }, [sortBy]);
  // View mode: "group" (the default) sections items by user-defined group
  // (Today / This Week / etc.). "company" reorganises the same items into
  // a kanban-like layout where each company gets its own section, and
  // group becomes a per-row chip. Also persisted.
  const [groupBy, setGroupBy] = useState(() => {
    try { return localStorage.getItem('todo_group_by') || 'group'; }
    catch (e) { return 'group'; }
  });
  useEffect(() => {
    try { localStorage.setItem('todo_group_by', groupBy); } catch (e) {}
  }, [groupBy]);
  // Drag-reorder transient state. dragId is the uuid of the item being
  // dragged; dragOverId is the uuid of the item under the cursor.
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  // Modals & inline forms
  const newItemDisc = useDisclosure();
  const existingDisc = useDisclosure();
  const groupsDisc = useDisclosure();
  const [editItem, setEditItem] = useState(null);
  const [subTodoParent, setSubTodoParent] = useState(null);
  const [holdItem, setHoldItem] = useState(null); // for the hold-reason modal
  // Item the user clicked the tick on while it was at 100% / done-visible.
  // A confirmation modal asks whether to hide it from the default list.
  const [hideConfirmItem, setHideConfirmItem] = useState(null);

  // Context menu (right-click on a TODO row)
  const [menu, setMenu] = useState(null); // { item, x, y }
  const menuRef = useRef(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      // Build the query string. user_id is included only when explicitly
      // viewing someone else's list. Backend defaults to caller's own.
      const qsParts = [
        showDone   ? 'include_done=true'   : '',
        showHidden ? 'include_hidden=true' : '',
        viewingOther ? `user_id=${encodeURIComponent(selectedUserId)}` : '',
      ].filter(Boolean);
      const qs = qsParts.length ? '?' + qsParts.join('&') : '';
      const groupsQs = viewingOther ? `?user_id=${encodeURIComponent(selectedUserId)}` : '';
      const [itemsR, groupsR] = await Promise.all([
        api.get(`/todo-items${qs}`),
        api.get(`/todo-groups${groupsQs}`),
      ]);
      setItems(Array.isArray(itemsR.data) ? itemsR.data : []);
      setGroups(Array.isArray(groupsR.data) ? groupsR.data : []);
    } catch (e) {
      const msg = e.response?.data?.error || e.message || 'Failed to load TODO list';
      toast({ title: msg, status: 'error', duration: 6000 });
    } finally { setLoading(false); }
  }, [showDone, showHidden, viewingOther, selectedUserId, toast]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Build the sections list according to the current view mode.
  //   - "group"   : one section per user-defined group (Today / This Week / …)
  //   - "company" : one section per company (alphabetical), plus an
  //                 "(No company)" bucket for items without a company link.
  // The returned shape is the same in both modes — { kind, group?, company?,
  // items[] } — so GroupSection can render them uniformly. The drag-reorder
  // and the manual sort still operate per-section.
  const sections = useMemo(() => {
    // Two independent toggles. An item is in view when neither of its
    // hidden states is active OR the corresponding toggle is on.
    const visible = items.filter(it =>
      (showDone   || !it.is_done) &&
      (showHidden || !it.is_hidden));
    const tops = classifyItems(visible, sortBy);

    if (groupBy === 'company') {
      // Bucket top-level items by company_name (id-keyed map so we don't
      // collapse two companies that happen to share a name).
      const byCompany = new Map();
      tops.forEach(t => {
        const key = t.company_id || '__none__';
        if (!byCompany.has(key)) {
          byCompany.set(key, {
            id: t.company_id || null,
            name: t.company_name || null,
            items: [],
          });
        }
        byCompany.get(key).items.push(t);
      });
      // Sort sections alphabetically by company name, with "(No company)"
      // pinned to the end so it doesn't clutter the top.
      const arr = Array.from(byCompany.values());
      arr.sort((a, b) => {
        if (!a.name && !b.name) return 0;
        if (!a.name) return 1;
        if (!b.name) return -1;
        return a.name.localeCompare(b.name);
      });
      return arr.map(c => ({
        kind: 'company',
        company: { id: c.id, name: c.name },
        items: c.items,
      }));
    }

    // Default: by group.
    const byGroup = new Map();
    tops.forEach(t => {
      const k = t.group_id || '__none__';
      if (!byGroup.has(k)) byGroup.set(k, []);
      byGroup.get(k).push(t);
    });
    const out = [];
    for (const g of groups) {
      const arr = byGroup.get(g.id) || [];
      if (arr.length === 0) continue;
      out.push({ kind: 'group', group: g, items: arr });
    }
    if (byGroup.has('__none__')) {
      out.push({ kind: 'group', group: null, items: byGroup.get('__none__') });
    }
    return out;
  }, [items, groups, showDone, showHidden, sortBy, groupBy]);

  const totalCount = items.filter(it => !it.is_done).length;

  // ─── CRUD handlers ─────────────────────────────────────────────────────────
  // Helper: short-circuit any write attempt when viewing another user's
  // list. The backend would refuse anyway (the routes scope to req.user)
  // but a toast + no network call is clearer UX. Also covers the case
  // where the user changed the dropdown mid-operation.
  const blockIfViewingOther = () => {
    if (!viewingOther) return false;
    toast({
      title: 'Read-only',
      description: "You're viewing someone else's TODO list. Switch back to your own to make changes.",
      status: 'info', duration: 3500,
    });
    return true;
  };

  const handleCreate = async (payload) => {
    if (blockIfViewingOther()) throw new Error('read-only');
    try {
      await api.post('/todo-items', payload);
      await fetchAll();
    } catch (e) {
      const msg = e.response?.data?.error || 'Failed to create';
      toast({ title: msg, status: 'error', duration: 4000 });
      throw e;
    }
  };

  const handleUpdate = async (id, patch) => {
    if (blockIfViewingOther()) return;
    try {
      await api.put(`/todo-items/${id}`, patch);
      await fetchAll();
    } catch (e) {
      const msg = e.response?.data?.error || 'Failed to update';
      toast({ title: msg, status: 'error', duration: 4000 });
    }
  };

  const handleDelete = async (id) => {
    if (blockIfViewingOther()) return;
    if (!window.confirm('Delete this TODO?')) return;
    try {
      await api.delete(`/todo-items/${id}`);
      await fetchAll();
    } catch (e) {
      const msg = e.response?.data?.error || 'Failed to delete';
      toast({ title: msg, status: 'error', duration: 4000 });
    }
  };

  // ─── Drag-reorder ─────────────────────────────────────────────────────────
  // The user drags one item onto another. Three cases the handler covers:
  //
  //  1. Same section: reorder within that section (the original case).
  //  2. Different group, group view: change the dragged item's group_id to
  //     match the target section, then reorder.
  //  3. Different company, company view: NOT supported. Changing company_id
  //     by drag would silently retag an item to a company it has no real
  //     link to — too easy to do by accident. Cross-company moves must
  //     happen through the Edit modal.
  //
  // Only fires when sortBy === 'manual' (auto-sort modes own the order).
  // `targetSection` is the section object the drop happened in:
  //     { kind, group?, company?, items: [...] }
  const handleDrop = useCallback(async (targetId, targetSection) => {
    if (!dragId || sortBy !== 'manual' || dragId === targetId) {
      setDragId(null); setDragOverId(null); return;
    }
    if (viewingOther) {
      // Read-only mode — dragging silently no-ops. (Drag attributes are
      // disabled lower down too, but this is a belt-and-braces guard
      // in case the user switched the picker mid-drag.)
      setDragId(null); setDragOverId(null); return;
    }
    if (!targetSection || !targetSection.kind) {
      // Defensive: a malformed descriptor would otherwise silently dump the
      // item into ungrouped because group_id resolves to undefined → null.
      setDragId(null); setDragOverId(null); return;
    }
    // Find the dragged item to check its current group/company.
    const dragged = items.find(it => it.id === dragId);
    if (!dragged) { setDragId(null); setDragOverId(null); return; }

    // Determine cross-section behaviour.
    // payload.group_id starts as "don't touch" (undefined). We only set it
    // when we genuinely want to move the item — never as a side effect of
    // a malformed descriptor.
    let payloadGroupChange = undefined;
    if (targetSection.kind === 'group') {
      const targetGroupId = targetSection.group ? targetSection.group.id : null;
      if (!sameId(dragged.group_id, targetGroupId)) {
        payloadGroupChange = targetGroupId; // null means "Ungrouped"
      }
    } else if (targetSection.kind === 'company') {
      const targetCompanyId = targetSection.company ? targetSection.company.id : null;
      if (!sameId(dragged.company_id, targetCompanyId)) {
        // Refuse to silently retag company. Tell the user explicitly.
        toast({
          title: 'To move to a different company, edit the item',
          description: 'Drag-reorder inside a company only — use Edit to change the company link.',
          status: 'info', duration: 4000,
        });
        setDragId(null); setDragOverId(null);
        return;
      }
    }

    // Build new ordering of the target section's items, with the dragged
    // item inserted at the target position (or appended if dropped on the
    // section background — targetId === null).
    const ids = targetSection.items.map(it => it.id).filter(id => id !== dragId);
    if (targetId) {
      const targetIdx = ids.indexOf(targetId);
      if (targetIdx === -1) ids.push(dragId);
      else                  ids.splice(targetIdx, 0, dragId);
    } else {
      ids.push(dragId);
    }
    setDragId(null); setDragOverId(null);

    try {
      // Single atomic request — reorder + optional group reassignment in
      // one shot. The two-request version (PUT then POST /reorder) had a
      // race where if the first response landed but a refetch slipped in
      // before the second, the row would briefly render in the source
      // section then jump.
      const body = { ordered_ids: ids };
      if (payloadGroupChange !== undefined) body.group_id = payloadGroupChange;
      await api.post(`/todo-items/${dragId}/reorder`, body);
      await fetchAll();
    } catch (e) {
      toast({ title: 'Reorder failed', status: 'error', duration: 3000 });
    }
  }, [dragId, sortBy, items, fetchAll, toast, viewingOther]);

  const sortLabel = {
    manual:   'Manual order',
    deadline: 'Deadline',
    added:    'Date added (newest)',
    company:  'Company (A–Z)',
  }[sortBy] || sortBy;

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <Box>
      <Flex align="center" mb={4} gap={4}>
        <IconButton icon={<FiArrowLeft />} variant="ghost" size="sm" onClick={onBack} aria-label="Back" />
        <HStack>
          <Text fontSize="lg" fontWeight="700">To Do List</Text>
          <Badge colorScheme="teal" fontSize="11px">{totalCount} items</Badge>
          {viewingOther && (
            <Badge colorScheme="orange" fontSize="10px">read-only</Badge>
          )}
        </HStack>
        {canPickUser && users.length > 0 && (
          /* User picker — switch the list to view another user's TODOs.
             Only rendered when the admin has the see_others_todo feature
             on, or when the caller is admin. Read-only when not self. */
          <HStack ml={4} spacing={1}>
            <Text fontSize="11px" color="gray.500">User:</Text>
            <Select size="sm" w="220px"
              value={selectedUserId || user?.id || ''}
              onChange={(e) => {
                const v = e.target.value;
                setSelectedUserId(v === user?.id ? null : v);
              }}>
              <option value={user?.id || ''}>
                {user?.display_name ? `${user.display_name} (me)` : 'Me'}
              </option>
              {users.filter(u => u.id !== user?.id).map(u => (
                <option key={u.id} value={u.id}>{u.display_name || u.email}</option>
              ))}
            </Select>
          </HStack>
        )}
        <Box flex={1} />
        <HStack>
          <Button size="sm" leftIcon={<FiPlus />} colorScheme="teal"
            isDisabled={viewingOther} onClick={newItemDisc.onOpen}>
            New TODO
          </Button>
          <Button size="sm" leftIcon={<FiSearch />} variant="outline"
            isDisabled={viewingOther} onClick={existingDisc.onOpen}>
            Add existing task or sub-task
          </Button>
          <Button size="sm" leftIcon={<FiSettings />} variant="ghost"
            isDisabled={viewingOther} onClick={groupsDisc.onOpen}>
            Groups
          </Button>
          {/* View mode: group sections vs company sections. Both render
              the same column layout (Title / Company / Project / Deadline /
              Progress) — only the section grouping changes. */}
          <HStack spacing={1}>
            <Text fontSize="11px" color="gray.500">View:</Text>
            <Select size="sm" w="130px" value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
              <option value="group">By group</option>
              <option value="company">By company</option>
            </Select>
          </HStack>
          {/* Sort selector. Persists in localStorage. Manual mode unlocks
              drag-reorder; other modes use computed sort. */}
          <HStack spacing={1}>
            <Text fontSize="11px" color="gray.500">Sort:</Text>
            <Select size="sm" w="170px" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="manual">Manual order</option>
              <option value="deadline">Deadline (earliest)</option>
              <option value="added">Date added (newest)</option>
              <option value="company">Company (A–Z)</option>
            </Select>
          </HStack>
          <Tooltip label="Show items completed and then hidden">
            <Button size="sm" variant={showDone ? 'solid' : 'ghost'}
              colorScheme={showDone ? 'green' : 'gray'}
              onClick={() => setShowDone(v => !v)}>
              {showDone ? 'Hide done' : 'Show done'}
            </Button>
          </Tooltip>
          <Tooltip label="Show items manually hidden (not completed)">
            <Button size="sm" variant={showHidden ? 'solid' : 'ghost'}
              colorScheme={showHidden ? 'purple' : 'gray'}
              onClick={() => setShowHidden(v => !v)}>
              {showHidden ? 'Hide hidden' : 'Show hidden'}
            </Button>
          </Tooltip>
        </HStack>
      </Flex>

      {loading ? (
        <Flex justify="center" py={10}><Spinner size="lg" color="teal.500" /></Flex>
      ) : sections.length === 0 ? (
        <Box textAlign="center" py={10} color="gray.500">
          <Text>No TODO items yet.</Text>
          <Text fontSize="sm" mt={2}>Use "New TODO" or "Add existing task or sub-task" to get started.</Text>
        </Box>
      ) : (
        <VStack align="stretch" spacing={4}>
          {sections.map((s, idx) => (
            <GroupSection
              key={s.kind + ':' + (s.group?.id || s.company?.id || idx)}
              kind={s.kind}
              group={s.group}
              company={s.company}
              items={s.items}
              groups={groups}
              groupBy={groupBy}
              sortBy={sortBy}
              readOnly={viewingOther}
              dragId={dragId}
              dragOverId={dragOverId}
              setDragId={setDragId}
              setDragOverId={setDragOverId}
              onDrop={handleDrop}
              onContextMenu={(item, x, y) => setMenu({ item, x, y })}
              onUpdate={handleUpdate}
              onAskHide={(item) => setHideConfirmItem(item)}
              onNavigateToProject={onNavigateToProject}
            />
          ))}
        </VStack>
      )}

      {/* Actions — centered modal so it is never clipped at the screen edge */}
      <Modal isOpen={!!menu} onClose={() => setMenu(null)} size="md" isCentered scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          {menu && (<>
          <ModalHeader fontSize="sm" pr={10}><Text noOfLines={2}>{menu.item.title}</Text></ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={4}>
            <VStack align="stretch" spacing={0}>
            {/* Completion shortcuts */}
            {[0, 25, 50, 75, 100].map(pct => (
              <Box key={pct} px={3} py={1.5} fontSize="12px" cursor="pointer"
                _hover={{ bg: 'blue.50' }}
                onClick={() => { handleUpdate(menu.item.id, { completion_percent: pct }); setMenu(null); }}>
                {menu.item.completion_percent === pct ? '✓ ' : ''}{pct}% complete{pct === 100 ? ' (done)' : ''}
              </Box>
            ))}
            <Divider my={1} />
            {!menu.item.parent_todo_id && (
              <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'teal.50' }} color="teal.700"
                onClick={() => { setSubTodoParent(menu.item); setMenu(null); }}>
                <FiCornerDownRight style={{ display: 'inline', marginRight: 6 }} />Add sub-TODO
              </Box>
            )}
            <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'gray.50' }}
              onClick={() => { setEditItem(menu.item); setMenu(null); }}>
              <FiEdit2 style={{ display: 'inline', marginRight: 6 }} />Edit (incl. deadline)
            </Box>
            {/* Hold-with-reason: parks the item without losing progress.
                Held items render with a yellow band on the row so they're
                visually distinct from active work. */}
            {menu.item.is_held ? (
              <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'yellow.50' }} color="yellow.700"
                onClick={async () => {
                  await handleUpdate(menu.item.id, { is_held: false, hold_reason: null, hold_explanation: null });
                  setMenu(null);
                }}>
                <FiPlay style={{ display: 'inline', marginRight: 6 }} />Resume (clear hold)
              </Box>
            ) : (
              <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'yellow.50' }} color="yellow.700"
                onClick={() => { setHoldItem(menu.item); setMenu(null); }}>
                <FiPause style={{ display: 'inline', marginRight: 6 }} />Hold with reason…
              </Box>
            )}
            {/* Group switcher */}
            <Box px={3} py={1} fontSize="10px" color="gray.400" fontWeight="600" textTransform="uppercase" mt={1}>
              Move to group
            </Box>
            {groups.map(g => (
              <Box key={g.id} px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'gray.50' }}
                onClick={() => { handleUpdate(menu.item.id, { group_id: g.id }); setMenu(null); }}>
                {sameId(menu.item.group_id, g.id) ? '✓ ' : ''}
                {g.color && <Box display="inline-block" w={2} h={2} bg={g.color} borderRadius="full" mr={2} />}
                {g.name}
              </Box>
            ))}
            {menu.item.group_id && (
              <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'gray.50' }}
                onClick={() => { handleUpdate(menu.item.id, { group_id: null }); setMenu(null); }}>
                Remove from group
              </Box>
            )}
            <Divider my={1} />
            {/* Hide / Unhide — separate from Done. Hide parks the row
                from view without claiming any progress on it. Unhide
                restores it. Cascades to sub-TODOs server-side. */}
            {menu.item.is_hidden ? (
              <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'purple.50' }} color="purple.700"
                onClick={async () => {
                  await handleUpdate(menu.item.id, { is_hidden: false });
                  setMenu(null);
                }}>
                <FiEye style={{ display: 'inline', marginRight: 6 }} />Unhide
              </Box>
            ) : (
              <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'purple.50' }} color="purple.700"
                onClick={async () => {
                  await handleUpdate(menu.item.id, { is_hidden: true });
                  setMenu(null);
                }}>
                <FiEyeOff style={{ display: 'inline', marginRight: 6 }} />
                Hide from list{!menu.item.parent_todo_id ? ' (incl. sub-TODOs)' : ''}
              </Box>
            )}
            <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'red.50' }} color="red.600"
              onClick={() => { handleDelete(menu.item.id); setMenu(null); }}>
              <FiTrash2 style={{ display: 'inline', marginRight: 6 }} />
              Delete{(menu.item.linked_task_id || menu.item.linked_subtask_id) ? ' (project copy stays)' : ''}
            </Box>
            </VStack>
          </ModalBody>
          </>)}
        </ModalContent>
      </Modal>

      {/* New TODO modal */}
      <NewTodoModal isOpen={newItemDisc.isOpen} onClose={newItemDisc.onClose}
        groups={groups} onCreate={handleCreate} />

      {/* Add existing modal */}
      <ExistingPickerModal isOpen={existingDisc.isOpen} onClose={existingDisc.onClose}
        groups={groups} onCreate={handleCreate} />

      {/* Sub-TODO modal */}
      {subTodoParent && (
        <NewTodoModal isOpen={true} onClose={() => setSubTodoParent(null)}
          groups={groups} parentItem={subTodoParent}
          onCreate={async (payload) => { await handleCreate({ ...payload, parent_todo_id: subTodoParent.id }); setSubTodoParent(null); }} />
      )}

      {/* Edit modal */}
      {editItem && (
        <EditTodoModal isOpen={true} onClose={() => setEditItem(null)}
          item={editItem} groups={groups}
          onSave={async (patch) => { await handleUpdate(editItem.id, patch); setEditItem(null); }} />
      )}

      {/* Groups manager */}
      <GroupsManagerModal isOpen={groupsDisc.isOpen} onClose={groupsDisc.onClose}
        groups={groups} onChanged={fetchAll} />

      {/* Hold-with-reason modal */}
      {holdItem && (
        <HoldReasonModal isOpen={true} onClose={() => setHoldItem(null)}
          item={holdItem}
          onSave={async (reason, explanation) => {
            await handleUpdate(holdItem.id, { is_held: true, hold_reason: reason, hold_explanation: explanation });
            setHoldItem(null);
          }} />
      )}

      {/* Hide-from-list confirmation. Triggered when the user clicks the
          tick on a done-visible row. "Hide" sets is_done=true (removes
          from default view). "Don't hide" closes without changes. */}
      {hideConfirmItem && (
        <HideConfirmModal isOpen={true} onClose={() => setHideConfirmItem(null)}
          item={hideConfirmItem}
          onHide={async () => {
            await handleUpdate(hideConfirmItem.id, { is_done: true });
            setHideConfirmItem(null);
          }} />
      )}
    </Box>
  );
}

// ─── GroupSection ────────────────────────────────────────────────────────────
// Renders a section header + a column-based table of items. Works for both
// view modes:
//   - kind='group':   coloured banner with the user-defined group name, the
//                     "Company" column is shown inline on each row.
//   - kind='company': company-name banner, the "Company" column is hidden
//                     (it's the section header) and a "Group" column appears
//                     inline instead, so users still see which group each
//                     item belongs to without losing alignment.
// Top-level items are draggable when sortBy === 'manual'; children never are.
function GroupSection({ kind, group, company, items, groups, groupBy, sortBy, readOnly,
                       dragId, dragOverId, setDragId, setDragOverId, onDrop,
                       onContextMenu, onUpdate, onAskHide, onNavigateToProject }) {
  // Drag enabled only in manual sort mode AND when the page isn't in
  // read-only mode (viewing another user's list).
  const canDrag = sortBy === 'manual' && !readOnly;
  const isCompanyMode = kind === 'company';
  // Section banner colour. Group mode uses the group's own colour. Company
  // mode uses a neutral teal so distinct companies still feel like distinct
  // sections without each picking a random palette.
  const bannerBg    = isCompanyMode ? 'teal.600' : (group?.color || 'gray.100');
  const bannerColor = isCompanyMode ? 'white'    : (group?.color ? 'white' : 'gray.700');
  const bannerLabel = isCompanyMode ? (company?.name || '(No company)') : (group?.name || 'Ungrouped');

  // The section descriptor we hand to onDrop so it knows what bucket the
  // user dropped into. Includes the full items array for the reorder math.
  const sectionDescriptor = { kind, group, company, items };

  // Section-level drop handler. Fires when the user drops on the section
  // background (the gap between rows or after the last item). Row drops
  // stop propagation, so this only runs for true background drops. Passes
  // null as the target so the dragged item appends to the end.
  const onSectionDragOver = canDrag ? (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  } : undefined;
  const onSectionDrop = canDrag ? (e) => {
    e.preventDefault();
    onDrop(null, sectionDescriptor);
  } : undefined;

  return (
    <Box border="1px" borderColor="gray.200" borderRadius="lg" overflow="hidden" bg="white">
      <Flex bg={bannerBg} color={bannerColor}
        px={4} py={2} align="center" gap={3}>
        {isCompanyMode && <FiBriefcase />}
        <Text fontSize="13px" fontWeight="700" textTransform="uppercase" letterSpacing="0.5px">
          {bannerLabel}
        </Text>
        <Badge colorScheme="whiteAlpha" fontSize="10px">
          {items.length}
        </Badge>
      </Flex>

      {/* Column header row — labels for the table-like grid below. The
          Company / Group column swaps depending on view mode so the
          information already carried by the section header isn't
          repeated in every row. */}
      <Flex bg="gray.50" px={4} py={1.5} align="center" gap={3} borderBottom="1px" borderColor="gray.200"
        fontSize="10px" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="0.3px">
        <Box w="14px" flexShrink={0} />{/* drag handle slot */}
        <Box w="18px" flexShrink={0} />{/* checkbox slot */}
        <Box flex={1} minW={0}>Title</Box>
        <Box w="140px" flexShrink={0}>{isCompanyMode ? 'Group' : 'Company'}</Box>
        <Box w="140px" flexShrink={0}>Project</Box>
        <Box w="110px" flexShrink={0}>Deadline</Box>
        <Box w="80px"  flexShrink={0} textAlign="right">Progress</Box>
        <Box w="20px"  flexShrink={0} />{/* actions */}
      </Flex>

      <VStack align="stretch" spacing={0} divider={<Divider />}
        onDragOver={onSectionDragOver} onDrop={onSectionDrop}
        minH={items.length === 0 ? '40px' : undefined}>
        {items.map(it => (
          <React.Fragment key={it.id}>
            <TodoRow item={it} canDrag={canDrag} groupBy={groupBy} groups={groups}
              isDragging={dragId === it.id} isDragOver={dragOverId === it.id}
              onDragStart={() => setDragId(it.id)}
              onDragOver={() => setDragOverId(it.id)}
              onDragEnd={() => { setDragId(null); setDragOverId(null); }}
              onDrop={() => onDrop(it.id, sectionDescriptor)}
              onContextMenu={onContextMenu} onUpdate={onUpdate} onAskHide={onAskHide}
              onNavigateToProject={onNavigateToProject} />
            {it.children && it.children.length > 0 && it.children.map(child => (
              <TodoRow key={child.id} item={child} isChild canDrag={false} groupBy={groupBy} groups={groups}
                onContextMenu={onContextMenu}
                onUpdate={onUpdate} onAskHide={onAskHide} onNavigateToProject={onNavigateToProject} />
            ))}
          </React.Fragment>
        ))}
        {/* Empty-section drop hint. When a section has no items and the
            user is dragging from another section, this gives them a clear
            visual target. */}
        {canDrag && dragId && items.length === 0 && (
          <Box textAlign="center" py={6} color="gray.400" fontSize="11px" fontStyle="italic"
            border="2px dashed" borderColor="gray.200" m={2} borderRadius="md">
            Drop here to move into this section
          </Box>
        )}
      </VStack>
    </Box>
  );
}

// ─── TodoRow ─────────────────────────────────────────────────────────────────
// Column-aligned row. Each cell has a fixed width to match the header row
// above, so titles, companies, projects, and deadlines line up vertically
// down the section even when individual values are short or empty.
function TodoRow({ item, isChild, canDrag, groupBy, groups, isDragging, isDragOver,
                  onDragStart, onDragOver, onDragEnd, onDrop,
                  onContextMenu, onUpdate, onAskHide, onNavigateToProject }) {
  const isLinked = !!(item.linked_task_id || item.linked_subtask_id);
  const deadline = formatDeadline(item.deadline_date, item.deadline_time);
  // In company view the section header IS the company, so showing it again
  // in the row would waste a column. Instead surface the user's group there
  // — it's the most useful per-row info once company is implicit.
  const itemGroup = groupBy === 'company' && groups
    ? groups.find(g => sameId(g.id, item.group_id))
    : null;

  // ── Completion phase derivation ────────────────────────────────────────
  // Four user-facing states. Backend stores them as two independent fields
  // (completion_percent and is_done) — we collapse to a single `phase`
  // here for consistent styling and click behaviour.
  //
  //   pending          : 0%, no flags                → neutral text, white row
  //   inprogress       : 1-99%, no flags             → dark-orange text
  //   done_visible     : 100%, not done-hidden       → grey highlight + grey strikethrough text
  //   done_hidden      : is_done=true                → only renders if "Show done" toggle is on
  //   manually_hidden  : is_hidden=true (not done)   → only renders if "Show hidden" toggle is on
  // Priority: manually_hidden > done_hidden (a user-hidden row is hidden
  // regardless of progress, but we visually treat it as the parked state
  // rather than the completed-then-hidden state).
  let phase;
  if (item.is_hidden)                     phase = 'manually_hidden';
  else if (item.is_done)                  phase = 'done_hidden';
  else if ((item.completion_percent || 0) >= 100) phase = 'done_visible';
  else if ((item.completion_percent || 0) > 0)    phase = 'inprogress';
  else                                    phase = 'pending';

  // Per-phase styling. Keeps the JSX below readable.
  const styleByPhase = {
    pending:         { bg: 'white',      rowOpacity: 1,    textColor: 'gray.800',   textDecoration: 'none',         titleWeight: isChild ? '400' : '600' },
    inprogress:      { bg: 'white',      rowOpacity: 1,    textColor: 'orange.700', textDecoration: 'none',         titleWeight: isChild ? '500' : '700' },
    done_visible:    { bg: 'gray.100',   rowOpacity: 1,    textColor: 'gray.600',   textDecoration: 'line-through', titleWeight: isChild ? '400' : '500' },
    done_hidden:     { bg: 'gray.50',    rowOpacity: 0.55, textColor: 'gray.500',   textDecoration: 'line-through', titleWeight: isChild ? '400' : '500' },
    // Manually-hidden uses a purple tint (matches the "Show hidden" toggle's
    // colour) and italic text so it reads as "parked" rather than "done".
    manually_hidden: { bg: 'purple.50',  rowOpacity: 0.7,  textColor: 'purple.700', textDecoration: 'none',         titleWeight: isChild ? '400' : '500', fontStyle: 'italic' },
  };
  const ps = styleByPhase[phase];

  // Checkbox click cycle:
  //   pending / inprogress → set to 100% (done_visible)
  //   done_visible         → ask the user if they want to hide it
  //   hidden               → unhide (clear is_done; row reappears at 100%)
  //
  // The "ask to hide" step bubbles up to the parent so it can render the
  // confirm modal — TodoRow stays presentational.
  //   pending / inprogress → set to 100% (done_visible)
  //   done_visible         → ask the user if they want to hide it (done flow)
  //   done_hidden          → unhide (clear is_done; row reappears at 100%)
  //   manually_hidden      → unhide (clear is_hidden; row reappears at its prior progress)
  //
  // The "ask to hide" step bubbles up to the parent so it can render the
  // confirm modal — TodoRow stays presentational.
  const handleCheckboxClick = () => {
    if (phase === 'done_hidden') {
      onUpdate(item.id, { is_done: false });
    } else if (phase === 'manually_hidden') {
      onUpdate(item.id, { is_hidden: false });
    } else if (phase === 'done_visible') {
      onAskHide && onAskHide(item);
    } else {
      onUpdate(item.id, { completion_percent: 100 });
    }
  };

  // Held items get a yellow left border so they're scannable in a long
  // list. Drag-over gets a teal indicator on the top edge.
  const bg = isDragOver ? 'teal.50' : ps.bg;

  return (
    <Flex
      px={4} py={2} align="center" gap={3}
      pl={isChild ? 12 : 4}
      bg={bg}
      opacity={ps.rowOpacity}
      borderLeftColor="yellow.400" borderLeftWidth={item.is_held ? '3px' : '0'}
      borderTop={isDragOver ? '2px solid' : 'none'} borderTopColor="teal.400"
      _hover={{
        bg: phase === 'done_visible'    ? 'gray.200'
          : phase === 'done_hidden'     ? 'gray.100'
          : phase === 'manually_hidden' ? 'purple.100'
          :                                'gray.50'
      }}
      draggable={canDrag}
      onDragStart={canDrag ? (e) => { e.dataTransfer.effectAllowed = 'move'; onDragStart && onDragStart(); } : undefined}
      onDragOver={canDrag ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; onDragOver && onDragOver(); } : undefined}
      onDragEnd={canDrag ? () => onDragEnd && onDragEnd() : undefined}
      onDrop={canDrag ? (e) => { e.preventDefault(); e.stopPropagation(); onDrop && onDrop(); } : undefined}
      onContextMenu={(e) => { e.preventDefault(); onContextMenu(item, e.clientX, e.clientY); }}>

      {/* Drag handle. Only shown when canDrag (manual sort mode). For
          children, it's a thin spacer so column alignment matches the
          parent rows. */}
      <Box w="14px" flexShrink={0} display="flex" alignItems="center" justifyContent="center">
        {canDrag && <FiMove color="gray" size={11} style={{ cursor: 'grab' }} />}
      </Box>

      {/* Checkbox.
          - pending: empty white square
          - inprogress: thin orange ring with a partial fill so the user
            sees progress at a glance
          - done_visible: solid green tick (still actionable — clicking
            again asks to hide)
          - done_hidden: hollow grey tick (clicking again restores to done_visible)
          - manually_hidden: empty purple-ringed square (clicking again unhides) */}
      <Tooltip label={
        phase === 'pending'         ? 'Mark complete' :
        phase === 'inprogress'      ? `In progress (${item.completion_percent}%) — click to mark complete` :
        phase === 'done_visible'    ? 'Complete — click to hide from list' :
        phase === 'manually_hidden' ? 'Hidden — click to restore' :
                                      'Done & hidden — click to restore'
      }>
        <Box
          as="button" w="18px" h="18px" border="2px"
          borderColor={
            phase === 'done_visible'    ? 'green.400' :
            phase === 'inprogress'      ? 'orange.400' :
            phase === 'manually_hidden' ? 'purple.400' :
            phase === 'done_hidden'     ? 'gray.400'   : 'gray.300'
          }
          borderRadius="md"
          bg={phase === 'done_visible' ? 'green.400' : 'white'}
          display="flex" alignItems="center" justifyContent="center" flexShrink={0}
          position="relative" overflow="hidden"
          onClick={handleCheckboxClick}>
          {/* in-progress partial fill */}
          {phase === 'inprogress' && (
            <Box position="absolute" left={0} bottom={0} right={0}
              h={`${item.completion_percent}%`} bg="orange.300" />
          )}
          {phase === 'done_visible'    && <FiCheck size={11} color="white" />}
          {phase === 'done_hidden'     && <FiCheck size={11} color="gray" />}
          {phase === 'manually_hidden' && <FiEyeOff size={10} color="#805AD5" />}
        </Box>
      </Tooltip>

      {/* Title column */}
      <Box flex={1} minW={0}>
        <HStack spacing={2} align="center">
          {isChild && <FiCornerDownRight color="gray" size={12} />}
          <Text
            fontSize="13px" fontWeight={ps.titleWeight}
            color={ps.textColor}
            textDecoration={ps.textDecoration}
            fontStyle={ps.fontStyle || 'normal'}
            isTruncated>
            {item.title}
          </Text>
          {isLinked && (
            <Tooltip label="Linked to an existing task / sub-task">
              <Tag size="sm" colorScheme="purple" variant="subtle">
                <FiLink size={10} />
              </Tag>
            </Tooltip>
          )}
          {!!item.is_held && (
            <Tooltip label={item.hold_explanation || item.hold_reason || 'On hold'}>
              <Tag size="sm" colorScheme="yellow" variant="subtle">
                <FiPause size={10} style={{ marginRight: 4 }} />
                <TagLabel fontSize="10px">held</TagLabel>
              </Tag>
            </Tooltip>
          )}
        </HStack>
        {item.description && (
          <Text fontSize="11px" color="gray.500" mt={1} noOfLines={2}>{item.description}</Text>
        )}
      </Box>

      {/* Company / Group column.
          In group view: show company (group is the section header).
          In company view: show group with its colour swatch (company is
          the section header). Either way the cell width stays at 140px so
          rows still line up across sections. */}
      <Box w="140px" flexShrink={0} minW={0}>
        {groupBy === 'company' ? (
          itemGroup ? (
            <HStack spacing={1.5} fontSize="11px" color="gray.700">
              {itemGroup.color && (
                <Box w={2} h={2} borderRadius="full" bg={itemGroup.color} flexShrink={0} />
              )}
              <Text isTruncated title={itemGroup.name}>{itemGroup.name}</Text>
            </HStack>
          ) : (
            <Text fontSize="11px" color="gray.300">—</Text>
          )
        ) : (
          item.company_name ? (
            <Text fontSize="11px" color="gray.700" isTruncated title={item.company_name}>
              <FiBriefcase style={{ display: 'inline', marginRight: 4 }} />{item.company_name}
            </Text>
          ) : (
            <Text fontSize="11px" color="gray.300">—</Text>
          )
        )}
      </Box>

      {/* Project column */}
      <Box w="140px" flexShrink={0} minW={0}>
        {item.project_name ? (
          <Text fontSize="11px" color="blue.600" isTruncated
            cursor={onNavigateToProject ? 'pointer' : 'default'}
            onClick={() => onNavigateToProject && onNavigateToProject(item.project_id)}
            title={item.project_name}>
            <FiFolder style={{ display: 'inline', marginRight: 4 }} />{item.project_name}
          </Text>
        ) : (
          <Text fontSize="11px" color="gray.300">—</Text>
        )}
      </Box>

      {/* Deadline column */}
      <Box w="110px" flexShrink={0}>
        {deadline ? (
          <Text fontSize="11px"
            color={deadline.isOverdue ? 'red.600' : (deadline.isToday ? 'orange.500' : 'gray.700')}
            fontWeight={deadline.isOverdue || deadline.isToday ? '600' : '400'}>
            <FiCalendar style={{ display: 'inline', marginRight: 4 }} />{deadline.label}
          </Text>
        ) : (
          <Text fontSize="11px" color="gray.300">—</Text>
        )}
      </Box>

      {/* Completion progress */}
      <Box w="80px" flexShrink={0}>
        <Progress value={item.completion_percent || 0} size="xs" colorScheme="teal" borderRadius="full" />
        <Text fontSize="9px" color="gray.500" textAlign="right" mt={0.5}>
          {item.completion_percent || 0}%
        </Text>
      </Box>

      {/* Actions */}
      <Box w="20px" flexShrink={0}>
        <Tooltip label="More actions (or right-click)">
          <IconButton icon={<FiMoreHorizontal />} size="xs" variant="ghost" aria-label="More"
            onClick={(e) => onContextMenu(item, e.clientX, e.clientY)} />
        </Tooltip>
      </Box>
    </Flex>
  );
}

// ─── NewTodoModal — create a standalone or tagged TODO ───────────────────────
// ─── AttributionPicker — company → project → task → sub-task selector, shared
//     by the New and Edit TODO modals. Lets you pin a TODO to an existing
//     task/sub-task under a project, or add a new task / sub-task inline.
//     Emits { company_id, project_id, linked_task_id, linked_subtask_id } (all
//     UUIDs) via onChange. Pass `initial` to prefill (edit mode). ────────────
function AttributionPicker({ initial, onChange }) {
  const [companyId, setCompanyId] = useState(initial?.company_id || '');
  const [companyName, setCompanyName] = useState(initial?.company_name || '');
  const [companyQuery, setCompanyQuery] = useState('');
  const [companyResults, setCompanyResults] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(initial?.project_id || '');
  const [tasks, setTasks] = useState([]);
  const [taskId, setTaskId] = useState(initial?.linked_task_id || '');
  const [subtasks, setSubtasks] = useState([]);
  const [subtaskId, setSubtaskId] = useState(initial?.linked_subtask_id || '');
  const [addingTask, setAddingTask] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [addingSub, setAddingSub] = useState(false);
  const [newSubTitle, setNewSubTitle] = useState('');
  const toast = useToast();

  // Bubble the current selection up whenever it changes.
  useEffect(() => {
    onChange({
      company_id: companyId || null,
      project_id: projectId || null,
      linked_task_id: subtaskId ? null : (taskId || null),
      linked_subtask_id: subtaskId || null,
    });
  }, [companyId, projectId, taskId, subtaskId]);

  // Edit prefill: load the cascade from the initial ids, once on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!initial?.company_id) return;
      try {
        const { data } = await api.get(`/projects?company_id=${initial.company_id}&limit=100`);
        if (cancelled) return;
        const active = (data || []).filter(p => !p.is_done && p.project_type !== 'workflow_template');
        setProjects(active);
        if (!initial.project_id) return;
        const p = active.find(x => sameId(x.id, initial.project_id));
        const embedded = Array.isArray(p?.tasks) ? p.tasks : [];
        if (embedded.length) setTasks(embedded);
        else { try { const r = await api.get(`/tasks?project_id=${encodeURIComponent(initial.project_id)}`); if (!cancelled) setTasks(Array.isArray(r.data) ? r.data : []); } catch(e){} }
        if (initial.linked_task_id) {
          try { const r = await api.get(`/sub-tasks?task_id=${encodeURIComponent(initial.linked_task_id)}`); if (!cancelled) setSubtasks(Array.isArray(r.data) ? r.data : []); } catch(e){}
        }
      } catch(e){}
    })();
    return () => { cancelled = true; };
  }, []);

  // Company search
  useEffect(() => {
    if (!companyQuery || companyQuery.length < 2 || companyName === companyQuery) { setCompanyResults([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/companies/search?q=${encodeURIComponent(companyQuery)}&limit=10`); if (!cancelled) setCompanyResults(Array.isArray(data) ? data : []); } catch(e){}
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [companyQuery, companyName]);

  const pickCompany = async (c) => {
    setCompanyId(c.id); setCompanyName(c.name); setCompanyQuery(''); setCompanyResults([]);
    setProjectId(''); setTasks([]); setTaskId(''); setSubtasks([]); setSubtaskId('');
    try { const { data } = await api.get(`/projects?company_id=${c.id}&limit=100`); setProjects((data || []).filter(p => !p.is_done && p.project_type !== 'workflow_template')); } catch(e){ setProjects([]); }
  };
  const clearCompany = () => { setCompanyId(''); setCompanyName(''); setCompanyQuery(''); setProjectId(''); setProjects([]); setTasks([]); setTaskId(''); setSubtasks([]); setSubtaskId(''); };

  const pickProject = async (pid) => {
    setProjectId(pid); setTaskId(''); setSubtasks([]); setSubtaskId(''); setAddingTask(false);
    const p = projects.find(x => sameId(x.id, pid));
    const embedded = Array.isArray(p?.tasks) ? p.tasks : [];
    if (embedded.length) { setTasks(embedded); return; }
    setTasks([]);
    if (!pid) return;
    try { const { data } = await api.get(`/tasks?project_id=${encodeURIComponent(pid)}`); setTasks(Array.isArray(data) ? data : []); } catch(e){ setTasks([]); }
  };
  const pickTask = async (tid) => {
    setTaskId(tid); setSubtaskId(''); setSubtasks([]); setAddingSub(false);
    if (!tid) return;
    try { const { data } = await api.get(`/sub-tasks?task_id=${encodeURIComponent(tid)}`); setSubtasks(Array.isArray(data) ? data : []); } catch(e){ setSubtasks([]); }
  };

  const addTask = async () => {
    const t = newTaskTitle.trim();
    if (!t || !projectId) return;
    try {
      const { data } = await api.post('/tasks', { project_id: projectId, task_title: t });
      setTasks(prev => [...prev, data]); setTaskId(data.id);
      setSubtasks([]); setSubtaskId(''); setNewTaskTitle(''); setAddingTask(false);
    } catch(e){ toast({ title: 'Could not add task', status: 'error', duration: 2000 }); }
  };
  const addSubtask = async () => {
    const t = newSubTitle.trim();
    if (!t || !taskId) return;
    try {
      const { data } = await api.post('/sub-tasks', { task_id: taskId, title: t });
      setSubtasks(prev => [...prev, data]); setSubtaskId(data.id);
      setNewSubTitle(''); setAddingSub(false);
    } catch(e){ toast({ title: 'Could not add sub-task', status: 'error', duration: 2000 }); }
  };

  return (
    <>
      <FormControl>
        <FormLabel fontSize="xs">Tag to company (optional)</FormLabel>
        {companyId ? (
          <HStack>
            <Tag colorScheme="orange"><TagLabel>{companyName || 'Company'}</TagLabel></Tag>
            <IconButton icon={<FiX />} size="xs" variant="ghost" onClick={clearCompany} aria-label="Clear" />
          </HStack>
        ) : (
          <Box position="relative">
            <Input size="sm" value={companyQuery} onChange={(e) => setCompanyQuery(e.target.value)} placeholder="Type a company name…" />
            {companyResults.length > 0 && (
              <Box position="absolute" top="100%" left={0} right={0} bg="white" border="1px" borderColor="gray.200" borderRadius="md" boxShadow="md" zIndex={10} maxH="200px" overflowY="auto" mt={1}>
                {companyResults.map(c => (
                  <Box key={c.id} px={3} py={2} fontSize="12px" cursor="pointer" _hover={{ bg: 'gray.50' }} onClick={() => pickCompany(c)}>{c.name}</Box>
                ))}
              </Box>
            )}
          </Box>
        )}
      </FormControl>
      {companyId && (
        <FormControl>
          <FormLabel fontSize="xs">…and project (optional)</FormLabel>
          {projects.length === 0 ? (
            <Text fontSize="11px" color="gray.500">No active projects under this company.</Text>
          ) : (
            <Select size="sm" value={projectId} onChange={(e) => pickProject(e.target.value)}>
              <option value="">(no project)</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.project_name}</option>)}
            </Select>
          )}
        </FormControl>
      )}
      {projectId && (
        <FormControl>
          <FormLabel fontSize="xs">…and task (optional)</FormLabel>
          <Select size="sm" value={taskId} onChange={(e) => pickTask(e.target.value)}>
            <option value="">{tasks.length === 0 ? '(no tasks yet)' : '(no task)'}</option>
            {tasks.map(t => <option key={t.id} value={t.id}>{t.task_title || t.title || 'Untitled task'}</option>)}
          </Select>
          {addingTask ? (
            <HStack mt={1}>
              <Input size="xs" autoFocus value={newTaskTitle} onChange={(e) => setNewTaskTitle(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') addTask(); }} placeholder="New task title…" />
              <Button size="xs" colorScheme="teal" onClick={addTask}>Add</Button>
              <Button size="xs" variant="ghost" onClick={() => { setAddingTask(false); setNewTaskTitle(''); }}>Cancel</Button>
            </HStack>
          ) : (
            <Button size="xs" variant="link" colorScheme="teal" mt={1} leftIcon={<FiPlus />} onClick={() => setAddingTask(true)}>Add a task to this project</Button>
          )}
        </FormControl>
      )}
      {taskId && (
        <FormControl>
          <FormLabel fontSize="xs">…and sub-task (optional)</FormLabel>
          {subtasks.length > 0 && (
            <Select size="sm" value={subtaskId} onChange={(e) => setSubtaskId(e.target.value)}>
              <option value="">(no sub-task)</option>
              {subtasks.map(s => <option key={s.id} value={s.id}>{s.title || s.name || 'Untitled sub-task'}</option>)}
            </Select>
          )}
          {addingSub ? (
            <HStack mt={1}>
              <Input size="xs" autoFocus value={newSubTitle} onChange={(e) => setNewSubTitle(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') addSubtask(); }} placeholder="New sub-task title…" />
              <Button size="xs" colorScheme="teal" onClick={addSubtask}>Add</Button>
              <Button size="xs" variant="ghost" onClick={() => { setAddingSub(false); setNewSubTitle(''); }}>Cancel</Button>
            </HStack>
          ) : (
            <Button size="xs" variant="link" colorScheme="teal" mt={1} leftIcon={<FiPlus />} onClick={() => setAddingSub(true)}>Add a sub-task to this task</Button>
          )}
        </FormControl>
      )}
    </>
  );
}

function NewTodoModal({ isOpen, onClose, groups, parentItem, onCreate }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [groupId, setGroupId] = useState('');
  const [attr, setAttr] = useState({});
  // Deadline split into date + time. Both optional. Default to blank — user
  // pulls them in only when the TODO has a real cutoff.
  const [deadlineDate, setDeadlineDate] = useState('');
  const [deadlineTime, setDeadlineTime] = useState('');
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setTitle(''); setDescription('');
      // Sub-TODO inherits the parent's group; otherwise default to the first
      // group ("Today") so items don't silently land in Ungrouped.
      const defaultGroup = parentItem?.group_id
        || (groups && groups.length > 0 ? groups[0].id : '');
      setGroupId(defaultGroup);
      setAttr({});
      setDeadlineDate(''); setDeadlineTime('');
    }
  }, [isOpen, parentItem, groups]);

  const handleSave = async () => {
    if (!title.trim()) { toast({ title: 'Title required', status: 'warning', duration: 1500 }); return; }
    setSaving(true);
    // Sub-TODOs inherit the parent's company/project; top-level TODOs use the
    // attribution picker's selection.
    const link = parentItem
      ? { company_id: parentItem.company_id || null, project_id: parentItem.project_id || null, linked_task_id: null, linked_subtask_id: null }
      : { company_id: attr.company_id || null, project_id: attr.project_id || null, linked_task_id: attr.linked_task_id || null, linked_subtask_id: attr.linked_subtask_id || null };
    try {
      await onCreate({
        title: title.trim(),
        description: description.trim() || null,
        group_id: groupId || null,
        ...link,
        deadline_date: deadlineDate || null,
        deadline_time: deadlineTime || null,
      });
      onClose();
    } catch (e) {} // toast handled upstream
    finally { setSaving(false); }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md">
          {parentItem ? `Add sub-TODO under "${parentItem.title}"` : 'New TODO'}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={3} align="stretch">
            <FormControl isRequired>
              <FormLabel fontSize="xs">Title</FormLabel>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="What needs doing?" autoFocus />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs">Description (optional)</FormLabel>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3}
                placeholder="More detail, links, notes…" fontSize="13px" />
            </FormControl>
            {/* Deadline — split date and time. Both optional. Splitting
                keeps "by end of day" implicit when no time is set, and
                stops timezone surprises that would come from a single
                datetime-local field. */}
            <HStack spacing={2}>
              <FormControl>
                <FormLabel fontSize="xs">Deadline date (optional)</FormLabel>
                <Input type="date" size="sm" value={deadlineDate}
                  onChange={(e) => setDeadlineDate(e.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs">Deadline time (optional)</FormLabel>
                <Input type="time" size="sm" value={deadlineTime}
                  onChange={(e) => setDeadlineTime(e.target.value)} isDisabled={!deadlineDate} />
              </FormControl>
            </HStack>
            {!parentItem && (
              <>
                <FormControl>
                  <FormLabel fontSize="xs">Group</FormLabel>
                  <Select value={groupId} onChange={(e) => setGroupId(e.target.value)} size="sm">
                    <option value="">(no group)</option>
                    {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                  </Select>
                </FormControl>
                <AttributionPicker key={isOpen ? 'open' : 'closed'} initial={null} onChange={setAttr} />
              </>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={2} onClick={onClose}>Cancel</Button>
          <Button colorScheme="teal" onClick={handleSave} isLoading={saving}>Create</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

// ─── EditTodoModal — edit title/description/group/etc. ───────────────────────
function EditTodoModal({ isOpen, onClose, item, groups, onSave }) {
  const [title, setTitle] = useState(item.title || '');
  const [description, setDescription] = useState(item.description || '');
  const [completion, setCompletion] = useState(item.completion_percent || 0);
  const [groupId, setGroupId] = useState(item.group_id || '');
  // MySQL returns DATE as "YYYY-MM-DDT00:00:00.000Z" sometimes; normalise to
  // "YYYY-MM-DD" for the date input. TIME comes back as "HH:MM:SS"; trim to
  // "HH:MM" for the time input.
  const initDate = item.deadline_date
    ? String(item.deadline_date).slice(0, 10) : '';
  const initTime = item.deadline_time
    ? String(item.deadline_time).slice(0, 5) : '';
  const [deadlineDate, setDeadlineDate] = useState(initDate);
  const [deadlineTime, setDeadlineTime] = useState(initTime);
  const [attr, setAttr] = useState({
    company_id: item.company_id || null,
    project_id: item.project_id || null,
    linked_task_id: item.linked_task_id || null,
    linked_subtask_id: item.linked_subtask_id || null,
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        title: title.trim(),
        description: description.trim() || null,
        completion_percent: parseInt(completion) || 0,
        group_id: groupId || null,
        company_id: attr.company_id || null,
        project_id: attr.project_id || null,
        linked_task_id: attr.linked_task_id || null,
        linked_subtask_id: attr.linked_subtask_id || null,
        deadline_date: deadlineDate || null,
        deadline_time: deadlineTime || null,
      });
    } catch (e) {}
    finally { setSaving(false); }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md">Edit TODO</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={3} align="stretch">
            <FormControl>
              <FormLabel fontSize="xs">Title</FormLabel>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs">Description</FormLabel>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} fontSize="13px" />
            </FormControl>
            <HStack spacing={2}>
              <FormControl>
                <FormLabel fontSize="xs">Deadline date</FormLabel>
                <Input type="date" size="sm" value={deadlineDate}
                  onChange={(e) => setDeadlineDate(e.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs">Deadline time</FormLabel>
                <Input type="time" size="sm" value={deadlineTime}
                  onChange={(e) => setDeadlineTime(e.target.value)} isDisabled={!deadlineDate} />
              </FormControl>
            </HStack>
            <FormControl>
              <FormLabel fontSize="xs">Completion %</FormLabel>
              <Input type="number" min={0} max={100} value={completion} onChange={(e) => setCompletion(e.target.value)} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs">Group</FormLabel>
              <Select value={groupId} onChange={(e) => setGroupId(e.target.value)} size="sm">
                <option value="">(no group)</option>
                {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </Select>
            </FormControl>
            <AttributionPicker
              initial={{
                company_id: item.company_id || '',
                company_name: item.company_name || '',
                project_id: item.project_id || '',
                linked_task_id: item.linked_task_id || '',
                linked_subtask_id: item.linked_subtask_id || '',
              }}
              onChange={setAttr}
            />
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={2} onClick={onClose}>Cancel</Button>
          <Button colorScheme="teal" onClick={handleSave} isLoading={saving}>Save</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

// ─── ExistingPickerModal — import a task or sub-task into TODO list ──────────
function ExistingPickerModal({ isOpen, onClose, groups, onCreate }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ tasks: [], sub_tasks: [] });
  const [searching, setSearching] = useState(false);
  const [groupId, setGroupId] = useState('');
  const toast = useToast();

  useEffect(() => {
    if (!isOpen) { setQuery(''); setResults({ tasks: [], sub_tasks: [] }); setGroupId(''); return; }
    // Default to the first available group on open — same rationale as the
    // New TODO modal: spare the user from accidentally creating ungrouped
    // items just because they didn't notice the dropdown.
    if (groups && groups.length > 0) setGroupId(groups[0].id);
  }, [isOpen, groups]);

  useEffect(() => {
    if (!query || query.length < 2) { setResults({ tasks: [], sub_tasks: [] }); return; }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/todo-items/search-existing?q=${encodeURIComponent(query)}&limit=20`);
        if (!cancelled) setResults(data || { tasks: [], sub_tasks: [] });
      } catch (e) {
        if (!cancelled) toast({ title: 'Search failed', status: 'error', duration: 2000 });
      } finally { if (!cancelled) setSearching(false); }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query, toast]);

  const importTask = async (t) => {
    try {
      await onCreate({
        linked_task_id: t.id,
        title: t.title,
        group_id: groupId || null,
        company_id: t.company_id || null,
        project_id: t.project_id || null,
      });
      onClose();
    } catch (e) {}
  };
  const importSub = async (s) => {
    try {
      await onCreate({
        linked_subtask_id: s.id,
        title: s.title,
        group_id: groupId || null,
        company_id: s.company_id || null,
        project_id: s.project_id || null,
      });
      onClose();
    } catch (e) {}
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md">Add existing task or sub-task</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={3} align="stretch">
            <FormControl>
              <FormLabel fontSize="xs">Add to group (optional)</FormLabel>
              <Select size="sm" value={groupId} onChange={(e) => setGroupId(e.target.value)}>
                <option value="">(no group)</option>
                {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </Select>
            </FormControl>
            <Input value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Search existing tasks and sub-tasks…" autoFocus />
            {searching && <Spinner size="sm" />}
            {!searching && query.length >= 2 && results.tasks.length === 0 && results.sub_tasks.length === 0 && (
              <Text fontSize="sm" color="gray.500">No matches.</Text>
            )}
            {results.tasks.length > 0 && (
              <Box>
                <Text fontSize="11px" color="gray.500" mb={1} fontWeight="600" textTransform="uppercase">
                  Tasks ({results.tasks.length})
                </Text>
                <VStack align="stretch" spacing={1}>
                  {results.tasks.map(t => (
                    <Flex key={t.id} p={2} border="1px" borderColor="gray.200" borderRadius="md" align="center"
                      _hover={{ bg: 'blue.50', borderColor: 'blue.300' }} cursor="pointer" onClick={() => importTask(t)}>
                      <Box flex={1}>
                        <Text fontSize="13px" fontWeight="600">{t.title}</Text>
                        <HStack spacing={1} mt={1}>
                          {t.company_name && <Tag size="sm" colorScheme="orange"><TagLabel fontSize="10px">{t.company_name}</TagLabel></Tag>}
                          {t.project_name && <Tag size="sm" colorScheme="blue"><TagLabel fontSize="10px">{t.project_name}</TagLabel></Tag>}
                        </HStack>
                      </Box>
                      <Button size="xs" colorScheme="teal" leftIcon={<FiPlus />}>Add</Button>
                    </Flex>
                  ))}
                </VStack>
              </Box>
            )}
            {results.sub_tasks.length > 0 && (
              <Box>
                <Text fontSize="11px" color="gray.500" mb={1} fontWeight="600" textTransform="uppercase">
                  Sub-tasks ({results.sub_tasks.length})
                </Text>
                <VStack align="stretch" spacing={1}>
                  {results.sub_tasks.map(s => (
                    <Flex key={s.id} p={2} border="1px" borderColor="gray.200" borderRadius="md" align="center"
                      _hover={{ bg: 'blue.50', borderColor: 'blue.300' }} cursor="pointer" onClick={() => importSub(s)}>
                      <Box flex={1}>
                        <Text fontSize="13px" fontWeight="600">{s.title}</Text>
                        <Text fontSize="10px" color="gray.500">In task: {s.task_title}</Text>
                        <HStack spacing={1} mt={1}>
                          {s.company_name && <Tag size="sm" colorScheme="orange"><TagLabel fontSize="10px">{s.company_name}</TagLabel></Tag>}
                          {s.project_name && <Tag size="sm" colorScheme="blue"><TagLabel fontSize="10px">{s.project_name}</TagLabel></Tag>}
                        </HStack>
                      </Box>
                      <Button size="xs" colorScheme="teal" leftIcon={<FiPlus />}>Add</Button>
                    </Flex>
                  ))}
                </VStack>
              </Box>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

// ─── GroupsManagerModal — manage user's groups ───────────────────────────────
function GroupsManagerModal({ isOpen, onClose, groups, onChanged }) {
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#4A5568');
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      await api.post('/todo-groups', { name: newName.trim(), color: newColor, sort_order: groups.length + 1 });
      setNewName(''); setNewColor('#4A5568');
      onChanged();
    } catch (e) {
      toast({ title: e.response?.data?.error || 'Failed', status: 'error', duration: 2000 });
    } finally { setSaving(false); }
  };

  const handleRename = async (g, name) => {
    if (!name.trim() || name === g.name) return;
    try { await api.put(`/todo-groups/${g.id}`, { name: name.trim() }); onChanged(); }
    catch (e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
  };

  const handleRecolor = async (g, color) => {
    try { await api.put(`/todo-groups/${g.id}`, { color }); onChanged(); }
    catch (e) {}
  };

  const handleDelete = async (g) => {
    if (!window.confirm(`Delete group "${g.name}"? Items in it become ungrouped.`)) return;
    try { await api.delete(`/todo-groups/${g.id}`); onChanged(); }
    catch (e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
  };

  const PRESETS = ['#F56565', '#ED8936', '#ECC94B', '#48BB78', '#4299E1', '#9F7AEA', '#ED64A6', '#4A5568'];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md">Manage groups</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={2} align="stretch">
            {groups.map(g => (
              <Flex key={g.id} p={2} border="1px" borderColor="gray.200" borderRadius="md" align="center" gap={2}>
                <Box w={3} h={3} borderRadius="full" bg={g.color || 'gray.300'} flexShrink={0} />
                <Input size="sm" defaultValue={g.name} onBlur={(e) => handleRename(g, e.target.value)} />
                <HStack spacing={0.5}>
                  {PRESETS.map(c => (
                    <Box key={c} w={4} h={4} bg={c} borderRadius="full" cursor="pointer"
                      border={g.color === c ? '2px solid black' : '1px solid #ccc'}
                      onClick={() => handleRecolor(g, c)} />
                  ))}
                </HStack>
                {!g.is_system && (
                  <IconButton icon={<FiTrash2 />} size="xs" variant="ghost" colorScheme="red"
                    onClick={() => handleDelete(g)} aria-label="Delete group" />
                )}
                {!!g.is_system && <Badge fontSize="9px" colorScheme="blue">System</Badge>}
              </Flex>
            ))}
            <Divider my={2} />
            <Text fontSize="xs" fontWeight="600" color="gray.600">Add a new group</Text>
            <Flex gap={2} align="center">
              <Input size="sm" value={newName} onChange={(e) => setNewName(e.target.value)}
                placeholder="Group name (e.g. Today, Errands, Long-term)" />
              <HStack spacing={0.5}>
                {PRESETS.map(c => (
                  <Box key={c} w={4} h={4} bg={c} borderRadius="full" cursor="pointer"
                    border={newColor === c ? '2px solid black' : '1px solid #ccc'}
                    onClick={() => setNewColor(c)} />
                ))}
              </HStack>
              <Button size="sm" colorScheme="teal" onClick={handleAdd} isLoading={saving} leftIcon={<FiPlus />}>
                Add
              </Button>
            </Flex>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

// ─── HoldReasonModal — capture why a TODO is being parked ────────────────────
// Mirrors the hold flow that already exists on sub-tasks. Reason is one of a
// short fixed set; explanation is free-form for the situational detail.
function HoldReasonModal({ isOpen, onClose, item, onSave }) {
  const [reason, setReason] = useState(item.hold_reason || 'waiting_on_client');
  const [explanation, setExplanation] = useState(item.hold_explanation || '');
  const [saving, setSaving] = useState(false);

  const REASONS = [
    { value: 'waiting_on_client',   label: 'Waiting on client' },
    { value: 'waiting_on_supplier', label: 'Waiting on supplier' },
    { value: 'waiting_on_us',       label: 'Waiting on us (internal)' },
    { value: 'blocked',             label: 'Blocked / other' },
  ];

  const handleSave = async () => {
    setSaving(true);
    try { await onSave(reason, explanation.trim() || null); }
    catch (e) {}
    finally { setSaving(false); }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md">Hold: {item.title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={3} align="stretch">
            <FormControl>
              <FormLabel fontSize="xs">Reason</FormLabel>
              <Select size="sm" value={reason} onChange={(e) => setReason(e.target.value)}>
                {REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs">Explanation (optional)</FormLabel>
              <Textarea size="sm" value={explanation} onChange={(e) => setExplanation(e.target.value)}
                placeholder="e.g. Awaiting purchase order from CFO" rows={3} />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={2} onClick={onClose}>Cancel</Button>
          <Button colorScheme="yellow" onClick={handleSave} isLoading={saving}>Hold</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

// ─── HideConfirmModal — second-click guard before hiding a done item ─────────
// The two-stage completion model means clicking the tick at 100% asks the
// user explicitly whether they want to hide the row from the default list.
// Standard "Hide" / "Don't hide" buttons — the latter is the modal's
// initialFocus so a stray Enter press is a no-op.
function HideConfirmModal({ isOpen, onClose, item, onHide }) {
  const dontHideRef = useRef(null);
  const [hiding, setHiding] = useState(false);
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm" initialFocusRef={dontHideRef}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md">Hide this item?</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="sm">
            Do you wish to hide <b>{item.title}</b> from the to-do list?
          </Text>
          <Text fontSize="xs" color="gray.500" mt={2}>
            It will still be available via the "Show done" toggle.
          </Text>
        </ModalBody>
        <ModalFooter>
          <Button ref={dontHideRef} variant="ghost" mr={2} onClick={onClose}>
            Don't hide
          </Button>
          <Button colorScheme="gray" isLoading={hiding}
            onClick={async () => { setHiding(true); try { await onHide(); } finally { setHiding(false); } }}>
            Hide
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
